import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import { Skeleton } from "@mui/material";
import { Box, Button, FlexColumn, FlexRow, FlexRowCenter, GridRow, Icon, PopupDialog, Text } from "designSystem/index";
import DojoView from "csi/page/DojoView";
import { routerStore } from "csi/state/rootStore";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent, MutableRefObject, ReactNode, useRef } from "react";
import { defProofProgressView } from "../ProofService";
import { useManageProofStore } from "./ManageProofStore";

interface ManageProofPopupProps {
	/** check if the pop up is open */
	open: boolean;
}

interface ManageProofDojoViewSkeletonProps {
	dojoViewDimensions: DOMRect;
}

const MANAGE_PROOF_DOJO_VIEW_COLUMNS = 4;

const ManageProofDojoViewSkeleton: FunctionComponent<ManageProofDojoViewSkeletonProps> = ({ dojoViewDimensions }) => {
	return (
		<GridRow
			grow
			spacing={1}
		>
			{Array.from({ length: MANAGE_PROOF_DOJO_VIEW_COLUMNS }, (el: ReactNode, i: number) => {
				return (
					<Skeleton
						height={dojoViewDimensions.height}
						key={i}
						variant="rectangular"
						width={(dojoViewDimensions.width - 12) / MANAGE_PROOF_DOJO_VIEW_COLUMNS}
					/>
				);
			})}
		</GridRow>
	);
};

export const ManageProofPopup: FunctionComponent<ManageProofPopupProps> = observer(({ open }) => {
	const {
		beginProofLockAction,
		beginProofTaskAction,
		closeManageProofPopup,
		dojoViewDimensions,
		dojoViewKey,
		isDojoViewLoading,
		isLoading,
		isProofLocked,
		onViewLoad,
		proofStatus,
		showDojoViewSkeleton,
		taskStatusColor,
		taskStatusIcon,
	} = useManageProofStore();
	const ref: MutableRefObject<HTMLElement> = useRef();

	function onViewLoaded() {
		onViewLoad(ref);
	}

	return (
		<PopupDialog
			// necessary to avoid structural conflict between dojo view and react
			disableEnforceFocus
			minWidth={674}
			onClose={closeManageProofPopup}
			open={open}
			titleRes="ManageProof"
		>
			{{
				main: (
					<FlexRow>
						<FlexColumn p={5}>
							<Box mb={5}>
								<FlexRowCenter justifyContent="space-between">
									<FlexRowCenter mt={1}>
										<Text
											bold
											mr={1}
											res="ProofStatus"
										/>
										<Icon sx={{ color: `${taskStatusColor} !important` }}>{taskStatusIcon}</Icon>
										{isLoading ? <Skeleton height={26} width={104} />
										: (
											<Text
												// wrap text in case of very long translation
												maxWidth={375}
												mx={1}
												res={proofStatus}
											/>
										)}
										{isProofLocked && (<LockIcon />)}
									</FlexRowCenter>
									<FlexRowCenter>
										{isLoading ? <Skeleton height={26} width={104} />
										: (
											<Button
												design="secondary"
												onClick={beginProofLockAction}
												res={isProofLocked ? "UnlockProof" : "LockProof"}
												startIcon={isProofLocked ? <LockOpenIcon /> : <LockIcon />}
											/>
										)}
									</FlexRowCenter>
								</FlexRowCenter>
							</Box>
							{showDojoViewSkeleton &&
								<ManageProofDojoViewSkeleton dojoViewDimensions={dojoViewDimensions} />
							}
							<Box
								ref={ref}
								sx={{
									position: isDojoViewLoading ? "absolute" : "",
									visibility: isDojoViewLoading ? "hidden" : "",
								}}
							>
								<DojoView
									fullHeight
									// included key to rerender the view, so the stage actions will appear or disappear based on whether the proof is locked
									key={dojoViewKey}
									nodeUrl={routerStore.locationUrl}
									onViewLoad={onViewLoaded}
									viewId="ProofManagement"
									viewNodeType="ProofWorkflowInstance"
									viewParams={{
										beginProofTaskAction,
										defProgressView: defProofProgressView(),
									}}
								/>
							</Box>
						</FlexColumn>
					</FlexRow>
				),
			}}
		</PopupDialog>
	);
});

export default ManageProofPopup;